import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import schoolService from "../../services/schoolService";
import classService from "../../services/classService";
import semesterService from "../../services/semesterService";
import subjectService from "../../services/subjectService";
import gradeService from "../../services/gradeService";
import { getGradeColorClasses, getGradeDescription, getGradeIcon, formatGrade, gradeScheme } from "../../utils/gradeColors";

const GradeManagement = () => {
  const { user } = useAuth();
  
  // State Management
  const [schools, setSchools] = useState([]);
  const [userClasses, setUserClasses] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Modal State
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [gradeFormData, setGradeFormData] = useState({
    description: "",
    gradeValue: "",
    examDate: "",
    weight: "1"
  });

  // Common weight options
  const weightOptions = [
    { value: "0.33", label: "1/3", display: "1/3" },
    { value: "0.5", label: "1/2", display: "1/2" },
    { value: "0.67", label: "2/3", display: "2/3" },
    { value: "1", label: "1", display: "1" },
    { value: "2", label: "2", display: "2" },
    { value: "3", label: "3", display: "3" }
  ];

  const loadStudentData = useCallback(async () => {
    try {
      setLoading(true);
      
      // 1. Fetch User's Classes
      const myClasses = await classService.getMyClasses();
      let currentClass = null;
      let currentSchool = null;
      let loadedSemesters = [];

      if (myClasses && myClasses.length > 0) {
        setUserClasses(myClasses);
        currentClass = myClasses[0];
        setSelectedClass(currentClass);

        // 2. Fetch School Details if available
        if (currentClass.schoolId) {
          try {
            const school = await schoolService.getSchoolById(currentClass.schoolId);
            if (school) {
              currentSchool = school;
              setSchools([school]);
              setSelectedSchool(school);
            }
          } catch (err) {
            console.warn("Could not fetch school details", err);
          }
        }
      }

      // 3. Fetch Semesters (if we have class info)
      if (currentClass && currentSchool) {
        try {
          const classSemesters = await semesterService.getSemestersByClass(currentSchool.id, currentClass.id);
          if (classSemesters && classSemesters.length > 0) {
            loadedSemesters = classSemesters;
            setSemesters(classSemesters);
            setSelectedSemester(classSemesters[0]);
          }
        } catch (err) {
          console.warn("Could not fetch semesters", err);
        }
      }

      // 4. Fetch Grades
      const existingGrades = await gradeService.getMyGrades();
      if (existingGrades) {
        setGrades(existingGrades);
        
        // Fallback: If no semesters found via API but grades exist, derive them
        if (loadedSemesters.length === 0 && existingGrades.length > 0) {
           const uniqueSemesters = [...new Set(existingGrades.map(g => g.semester).filter(Boolean))];
           if (uniqueSemesters.length > 0) {
             const derivedSemesters = uniqueSemesters.map((name, idx) => ({
               id: `derived-${idx}`,
               name: name
             }));
             setSemesters(derivedSemesters);
             setSelectedSemester(derivedSemesters[0]);
           }
        }
      }

    } catch (error) {
      console.error("Error loading student data:", error);
      setError("Failed to load your academic data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAdminData = useCallback(async () => {
    try {
      const schoolsResponse = await schoolService.getAllSchools();
      const schoolsData = Array.isArray(schoolsResponse) ? schoolsResponse : [];
      setSchools(schoolsData);
    } catch (error) {
      console.error("Error loading admin data:", error);
      setError("Error loading schools. Please try again later.");
    }
  }, []);

  // Load initial data
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      if (user?.roles?.some(role => role.name === 'LERNENDE')) {
        await loadStudentData();
      } else {
        await loadAdminData();
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
      setError("Error loading data. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [user, loadStudentData, loadAdminData]);

  useEffect(() => {
    if (user) {
      loadInitialData();
    }
  }, [user, loadInitialData]);

  const loadSemestersForClass = async (schoolId, classId) => {
    try {
      const semestersResponse = await semesterService.getSemestersByClass(schoolId, classId);
      setSemesters(semestersResponse || []);
      
      if (semestersResponse && semestersResponse.length > 0) {
        const firstSemester = semestersResponse[0];
        setSelectedSemester(firstSemester);
        await loadSubjectsAndGrades(schoolId, classId, firstSemester.id);
      }
    } catch (error) {
      console.error("Error loading semesters:", error);
      setError("Error loading semesters: " + error.message);
    }
  };

  const loadSubjectsAndGrades = async (schoolId, classId, semesterId) => {
    try {
      setLoading(true);
      
      const subjectsResponse = await subjectService.getSubjectsBySchoolClassSemester(schoolId, classId, semesterId);
      setSubjects(subjectsResponse || []);
      
      const gradesResponse = await gradeService.getGradesBySemester(semesterId);
      setGrades(gradesResponse || []);
      
    } catch (error) {
      console.error("Error loading subjects and grades:", error);
      setError("Error loading subjects and grades: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolChange = async (schoolId) => {
    try {
      setLoading(true);
      const school = schools.find(s => s.id === parseInt(schoolId));
      setSelectedSchool(school);
      setSelectedClass(null);
      setSemesters([]);
      setSubjects([]);
      setGrades([]);
      
      if (school) {
        const classesResponse = await classService.getClassesBySchool(school.id);
        setUserClasses(classesResponse || []);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
      setError("Error loading classes: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = async (classId) => {
    if (!selectedSchool) return;
    
    const classObj = userClasses.find(c => c.id === parseInt(classId));
    setSelectedClass(classObj);
    
    if (classObj) {
      await loadSemestersForClass(selectedSchool.id, classObj.id);
    }
  };

  const handleSemesterChange = async (semester) => {
    setSelectedSemester(semester);
    
    if (user?.roles?.some(role => role.name === 'LERNENDE')) {
      // For students: filter existing grades by semester
      const semesterGrades = grades.filter(g => g.semester === semester.name);
      setGrades(semesterGrades);
      
      // Get unique subjects from grades for this semester
      const subjectIds = [...new Set(semesterGrades.map(g => g.subjectId))];
      const uniqueSubjects = subjectIds.map(id => {
        const grade = semesterGrades.find(g => g.subjectId === id);
        return {
          id: id,
          name: grade.subjectName || `Subject ${id}`,
          description: `Subject for ${semester.name}`
        };
      });
      setSubjects(uniqueSubjects);
    } else {
      // For admins: load full data
      if (selectedSchool && selectedClass) {
        await loadSubjectsAndGrades(selectedSchool.id, selectedClass.id, semester.id);
      }
    }
  };

  const handleAddGrade = (subject) => {
    setSelectedSubject(subject);
    setGradeFormData({
      description: "",
      gradeValue: "",
      examDate: new Date().toISOString().split('T')[0],
      weight: "1",
      subjectName: subject.id === 'new' ? "" : subject.name
    });
    setShowGradeModal(true);
  };

  const handleSaveGrade = async () => {
    try {
      if (!gradeFormData.gradeValue || !gradeFormData.examDate) {
        setError("Please fill all required fields");
        return;
      }

      if (selectedSubject?.id === 'new' && !gradeFormData.subjectName?.trim()) {
        setError("Please enter a subject name");
        return;
      }

      const gradeValue = parseFloat(gradeFormData.gradeValue);
      if (isNaN(gradeValue) || gradeValue < 1 || gradeValue > 6) {
        setError("Grade must be a number between 1 and 6");
        return;
      }

      const gradeData = {
        description: gradeFormData.description.trim() || "",
        gradeValue: gradeValue,
        examDate: gradeFormData.examDate,
        weight: parseFloat(gradeFormData.weight),
        subjectId: selectedSubject.id === 'new' ? null : selectedSubject.id,
        subjectName: selectedSubject.id === 'new' ? gradeFormData.subjectName.trim() : selectedSubject.name,
        userId: user.id,
        semester: selectedSemester.name,
        academicYear: new Date().getFullYear() + "/" + (new Date().getFullYear() + 1)
      };

      await gradeService.createGrade(gradeData);
      setShowGradeModal(false);
      setError("");
      
      // Reload grades for students
      if (user?.roles?.some(role => role.name === 'LERNENDE')) {
        // Reload all grades and refresh display
        const updatedGrades = await gradeService.getMyGrades();
        setGrades(updatedGrades);
        
        // Update current semester view
        const currentSemesterGrades = updatedGrades.filter(g => g.semester === selectedSemester.name);
        setGrades(currentSemesterGrades);
        
        // Update subjects list
        const subjectIds = [...new Set(currentSemesterGrades.map(g => g.subjectId))];
        const uniqueSubjects = subjectIds.map(id => {
          const grade = currentSemesterGrades.find(g => g.subjectId === id);
          return {
            id: id,
            name: grade.subjectName || `Subject ${id}`,
            description: `Subject for ${selectedSemester.name}`
          };
        });
        setSubjects(uniqueSubjects);
      } else {
        // For admins: use full reload
        await loadSubjectsAndGrades(selectedSchool.id, selectedClass.id, selectedSemester.id);
      }
      
    } catch (error) {
      console.error("Error saving grade:", error);
      setError("Error saving grade: " + error.message);
    }
  };

  const calculateSubjectAverage = (subjectId) => {
    const subjectGrades = grades.filter(g => g.subjectId === subjectId);
    if (subjectGrades.length === 0) return null;
    
    const totalWeightedGrades = subjectGrades.reduce((sum, grade) => sum + (grade.gradeValue * grade.weight), 0);
    const totalWeight = subjectGrades.reduce((sum, grade) => sum + grade.weight, 0);
    
    return totalWeight > 0 ? (totalWeightedGrades / totalWeight).toFixed(2) : null;
  };

  const calculateSemesterAverage = () => {
    const subjectAverages = subjects.map(subject => calculateSubjectAverage(subject.id))
                                   .filter(avg => avg !== null)
                                   .map(avg => parseFloat(avg));
    
    if (subjectAverages.length === 0) return null;
    
    const total = subjectAverages.reduce((sum, avg) => sum + avg, 0);
    return (total / subjectAverages.length).toFixed(2);
  };

  const getSubjectGrades = (subjectId) => {
    return grades.filter(g => g.subjectId === subjectId).sort((a, b) => new Date(b.examDate) - new Date(a.examDate));
  };

  if (loading && schools.length === 0) {
    return (
      <div className="container-fluid p-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading grade data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="h3 mb-1 text-primary">
                <i className="fas fa-chart-line me-2"></i>
                Grade Management
              </h1>
              <p className="text-muted mb-0">
                {user?.roles?.some(role => role.name === 'LERNENDE') ? 'View and manage your grades' : 'Manage student grades'}
              </p>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {/* Grade Schema Legend */}
          <div className="card border-info mb-4">
            <div className="card-header bg-info text-white">
              <h6 className="mb-0">
                <i className="fas fa-graduation-cap me-2"></i>
                Swiss Grading System Reference
              </h6>
            </div>
            <div className="card-body">
              <div className="row">
                {gradeScheme.map((grade, index) => (
                  <div key={index} className="col-lg-3 col-md-4 col-sm-6 mb-2">
                    <div className="d-flex align-items-center">
                      <span className={`badge ${grade.color} me-2`}>
                        <i className={`${grade.icon} me-1`}></i>
                        {grade.range}
                      </span>
                      <small className="text-muted">{grade.description}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Student: No assignment found */}
          {!loading && schools.length === 0 && user?.roles?.some(role => role.name === 'LERNENDE') && (
            <div className="card border-warning">
              <div className="card-body text-center py-5">
                <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '4rem' }}></i>
                <h4 className="mt-3 text-warning">No Academic Assignment Found</h4>
                <p className="text-muted mb-4">
                  You have not been assigned to any school or class yet.
                </p>
                <div className="alert alert-info">
                  <strong>Your administrator needs to assign you to:</strong>
                  <ul className="mt-2 text-start">
                    <li>A school</li>
                    <li>A class within that school</li>
                    <li>Subjects for your semester</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Admin: School and Class Selection */}
          {!loading && schools.length > 0 && !user?.roles?.some(role => role.name === 'LERNENDE') && (
            <div className="card border-primary mb-4">
              <div className="card-body">
                <h5 className="card-title text-primary">Select School and Class to Manage Grades</h5>
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">School</label>
                    <select 
                      className="form-select"
                      onChange={(e) => handleSchoolChange(e.target.value)}
                      value={selectedSchool?.id || ""}
                    >
                      <option value="">Select a school...</option>
                      {schools.map(school => (
                        <option key={school.id} value={school.id}>
                          {school.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedSchool && (
                    <div className="col-md-6">
                      <label className="form-label">Class</label>
                      <select 
                        className="form-select"
                        onChange={(e) => handleClassChange(e.target.value)}
                        value={selectedClass?.id || ""}
                      >
                        <option value="">Select a class...</option>
                        {userClasses.map(classObj => (
                          <option key={classObj.id} value={classObj.id}>
                            {classObj.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* School and Class Info */}
          {selectedSchool && selectedClass && (
            <div className="card border-success mb-4">
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-md-6">
                    <h5 className="text-success mb-1">
                      <i className="fas fa-school me-2"></i>
                      {selectedSchool.name}
                    </h5>
                  </div>
                  <div className="col-md-6">
                    <h5 className="text-success mb-1">
                      <i className="fas fa-users me-2"></i>
                      {selectedClass.name}
                    </h5>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Semester Tabs */}
          {semesters.length > 0 && (
            <div className="card border-primary mb-4">
              <div className="card-body">
                <ul className="nav nav-tabs card-header-tabs" role="tablist">
                  {semesters.map((semester) => (
                    <li key={semester.id} className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${selectedSemester?.id === semester.id ? 'active' : ''}`}
                        onClick={() => handleSemesterChange(semester)}
                        type="button"
                        role="tab"
                      >
                        {semester.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Subjects and Grades */}
          {selectedSemester && (
            <div className="row">
              <div className="col-12">
                <div className="card border-primary">
                  <div className="card-header bg-primary text-white">
                    <div className="row align-items-center">
                      <div className="col">
                        <h5 className="mb-0">
                          <i className="fas fa-book me-2"></i>
                          {selectedSemester.name} - Subjects & Grades
                        </h5>
                      </div>
                      <div className="col-auto">
                        <span className="badge bg-light text-dark">
                          Semester Average: {calculateSemesterAverage() || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    {subjects.length === 0 ? (
                      <div className="text-center py-4">
                        <i className="fas fa-book text-muted" style={{ fontSize: '3rem' }}></i>
                        <h5 className="mt-3 text-muted">No Subjects Available</h5>
                        <p className="text-muted">No subjects found for this semester.</p>
                        {user?.roles?.some(role => role.name === 'LERNENDE') && (
                          <button
                            className="btn btn-primary mt-3"
                            onClick={() => handleAddGrade({ id: 'new', name: 'New Subject' })}
                          >
                            <i className="fas fa-plus me-2"></i>
                            Add Your First Grade
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Subject</th>
                              <th>SP</th>
                              <th>Grades</th>
                              <th>G</th>
                              <th>HJ</th>
                              <th>JG</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subjects.map((subject) => {
                              const subjectGrades = getSubjectGrades(subject.id);
                              const average = calculateSubjectAverage(subject.id);
                              
                              return (
                                <tr key={subject.id}>
                                  <td>
                                    <div>
                                      <strong>{subject.name}</strong>
                                      {subject.description && (
                                        <>
                                          <br />
                                          <small className="text-muted">{subject.description}</small>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                  <td>-</td>
                                  <td>
                                    {subjectGrades.length > 0 ? (
                                      <div className="d-flex flex-wrap gap-1">
                                        {subjectGrades.map((grade) => {
                                          const gradeColors = getGradeColorClasses(grade.gradeValue);
                                          return (
                                            <span
                                              key={grade.id}
                                              className={`badge ${gradeColors.badge}`}
                                              title={`${grade.description} (${grade.examDate}) - ${getGradeDescription(grade.gradeValue)}`}
                                            >
                                              <i className={`${getGradeIcon(grade.gradeValue)} me-1`}></i>
                                              {formatGrade(grade.gradeValue)}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td>
                                    {average ? (
                                      <span className={`badge ${getGradeColorClasses(average).badge}`}>
                                        <i className={`${getGradeIcon(average)} me-1`}></i>
                                        {formatGrade(average)}
                                      </span>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td>
                                    {average ? (
                                      <span className={`badge ${parseFloat(average) >= 4 ? 'bg-success' : 'bg-danger'}`}>
                                        {average}
                                      </span>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td>
                                    {average ? (
                                      <span className={`badge ${parseFloat(average) >= 4 ? 'bg-success' : 'bg-danger'}`}>
                                        {average}
                                      </span>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td>
                                    {user?.roles?.some(role => role.name === 'LERNENDE') && (
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => handleAddGrade(subject)}
                                        title="Add Grade"
                                      >
                                        <i className="fas fa-plus"></i>
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="table-light">
                            <tr>
                              <td colSpan="3"><strong>Semester Average</strong></td>
                              <td colSpan="3">
                                <span className={`badge ${calculateSemesterAverage() && parseFloat(calculateSemesterAverage()) >= 4 ? 'bg-success' : 'bg-danger'}`}>
                                  {calculateSemesterAverage() || 'N/A'}
                                </span>
                              </td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Grade Modal */}
      {showGradeModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header border-primary">
                <h5 className="modal-title text-primary">
                  <i className="fas fa-plus me-2"></i>
                  Add Grade for {selectedSubject?.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowGradeModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="mb-3">
                    <label className="form-label">Module/Subject *</label>
                    {selectedSubject?.id === 'new' ? (
                      <input
                        type="text"
                        className="form-control"
                        value={gradeFormData.subjectName || ""}
                        onChange={(e) => setGradeFormData({ ...gradeFormData, subjectName: e.target.value })}
                        placeholder="Enter subject name (e.g., Mathematics, Physics, etc.)"
                        required
                      />
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        value={selectedSubject?.name || ""}
                        disabled
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    )}
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Grade (1-6) *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={gradeFormData.gradeValue}
                      onChange={(e) => setGradeFormData({ ...gradeFormData, gradeValue: e.target.value })}
                      min="1"
                      max="6"
                      step="0.25"
                      placeholder="e.g., 5.5"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Weight</label>
                    <select
                      className="form-select"
                      value={gradeFormData.weight}
                      onChange={(e) => setGradeFormData({ ...gradeFormData, weight: e.target.value })}
                    >
                      <optgroup label="Simple Weightings">
                        {weightOptions.slice(0, 3).map(option => (
                          <option key={option.value} value={option.value}>
                            Weight: {option.display}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Standard Weightings">
                        {weightOptions.slice(3).map(option => (
                          <option key={option.value} value={option.value}>
                            Weight: {option.display}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <input
                      type="text"
                      className="form-control"
                      value={gradeFormData.description}
                      onChange={(e) => setGradeFormData({ ...gradeFormData, description: e.target.value })}
                      placeholder="e.g., Midterm Exam, Quiz, Assignment (optional)"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Exam Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={gradeFormData.examDate}
                      onChange={(e) => setGradeFormData({ ...gradeFormData, examDate: e.target.value })}
                      required
                    />
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowGradeModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveGrade}
                >
                  <i className="fas fa-save me-2"></i>
                  SAVE GRADE
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GradeManagement;